"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { CircleQuestionMark as HelpCircle, ArrowRight, MessageSquare } from 'lucide-react';
interface FaqItem {
  id: string;
  question: string;
  badge: string;
  answerPart1: string;
  linkText1?: string;
  linkTo1?: string;
  answerPart2?: string;
  linkText2?: string;
  linkTo2?: string;
  answerPart3?: string;
  bulletPoints: string[];
}
const faqItems: FaqItem[] = [{
  id: "item-1",
  question: "What's the difference between working with an SEO freelancer vs an agency?",
  badge: "Direct Collaboration",
  answerPart1: "You get direct communication with me, no account-manager overhead, faster decision-making, transparent reporting, and more personalized strategy. Agencies often have minimum budgets and longer approval cycles, whereas I handle your strategy and execution hands-on. You can connect directly through ",
  linkText1: "my consultation channel",
  linkTo1: "/ContactPage",
  answerPart2: " whenever you need answers.",
  bulletPoints: ["100% direct access to Yassine Essaadi without agency middlemen", "Agile execution, faster iterations, and zero approval bureaucracy", "Tailored strategies instead of generic retainer templates"]
}, {
  id: "item-2",
  question: "How much does SEO cost in Dubai?",
  badge: "Transparent Pricing",
  answerPart1: "Dubai freelance SEO retainers typically range from AED 2,000–10,000 per month depending on scope and competitiveness. A consultation helps determine the right investment for your goals, whether you need a dedicated technical turnaround, local map domination, or an enterprise-scale search campaign.",
  bulletPoints: ["Flexible monthly retainers adapted to your commercial scope", "No hidden agency overheads or locked long-term contracts", "Clear deliverables tied to organic search performance and ROI"]
}, {
  id: "item-3",
  question: "How long does SEO take to show results in Dubai's competitive market?",
  badge: "Timeline & Growth",
  answerPart1: "Competitive terms typically take 3-6 months. Less competitive keywords and technical fixes may show improvement within 6-10 weeks. Highly competitive sectors can take 6-12 months. Consistency and quality strategy matter more than speed, which is why I establish continuous tracking from week one.",
  bulletPoints: ["Technical indexation & crawl fixes show impact in 6–10 weeks", "Competitive commercial keywords accelerate between months 3 and 6", "High-competition sectors compound steadily over 6–12 months"]
}, {
  id: "item-4",
  question: "Can you help Dubai businesses target international customers?",
  badge: "Global Reach",
  answerPart1: "Yes. I develop international SEO strategies including country-specific keyword research, hreflang implementation, and localized landing pages. Whether you are expanding into the UK, Europe, the US, or across the Middle East, I ensure search engines deliver your content to the exact target territories.",
  bulletPoints: ["Precision hreflang, ccTLD, and subfolder architecture", "Territory-specific intent and commercial search mapping", "Multi-region technical indexing and localized content frameworks"]
}, {
  id: "item-5",
  question: "Can you help businesses outside Dubai?",
  badge: "UAE & GCC Reach",
  answerPart1: "Yes. I work with GCC businesses (Abu Dhabi, Sharjah, Saudi Arabia, Qatar, etc.) and international companies entering the UAE market, as well as Dubai-based businesses targeting UK, European, or international customers.",
  bulletPoints: ["Cross-emirate coverage for Abu Dhabi, Sharjah, and northern UAE", "Regional GCC expansion for Middle East market entrants", "Inbound UK, European, and global enterprise targeting"]
}, {
  id: "item-6",
  question: "How do you approach Local SEO for businesses across Dubai and the UAE?",
  badge: "Local Visibility",
  answerPart1: "Dubai is a high-density, hyper-local market. I tailor ",
  linkText1: "Local SEO strategies",
  linkTo1: "/LocalSEOPage",
  answerPart2: " specifically to Dubai's unique commercial dynamics. This covers Google Business Profile optimisation, localised intent targeting across districts like Downtown, Business Bay, Marina, and DIFC, citation building on regional directories, and local authority signals.",
  bulletPoints: ["Targeted Google Maps and 3-pack local visibility", "Area-specific district and community landing page frameworks", "Structured local schema markup and citation accuracy"]
}, {
  id: "item-7",
  question: "What is included in your comprehensive SEO Audit for Dubai websites?",
  badge: "In-Depth Diagnosis",
  answerPart1: "Every successful campaign starts with diagnostic clarity. Through my detailed ",
  linkText1: "SEO Audits",
  linkTo1: "/SeoAuditsPage",
  answerPart2: ", I uncover indexation blockers, rendering issues, international hreflang misconfigurations, Core Web Vitals bottlenecks, content gaps, and toxic backlink footprints. You receive an actionable, prioritised remediation roadmap rather than generic automated PDF reports.",
  bulletPoints: ["Crawl diagnostics and technical architecture review", "Competitor gap analysis and search intent mapping", "Practical step-by-step developer implementation guide"]
}, {
  id: "item-8",
  question: "Can you handle bilingual English and Arabic SEO requirements?",
  badge: "Multilingual SEO",
  answerPart1: "Yes. I structure multilingual setups with precise hreflang tags, dedicated URL taxonomy, and localised search intent mapping. Whether your target demographic searches in English or Arabic, I ensure search engines index and serve the correct linguistic versions without duplicate content penalties.",
  bulletPoints: ["Correct hreflang and canonical implementation", "Targeted intent research for English and Arabic queries", "Cultural and geographic search nuance alignment"]
}];
export default function DubaiFaq() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="dubaiseofaq" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="flex items-center justify-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-secondary text-secondary-foreground">
              <HelpCircle className="size-3.5 text-accent" />
              Dubai SEO Advisory
            </span>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-primary tracking-tight mb-6">
            Frequently Asked Questions About My Dubai SEO Consultancy
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2,
          ease: "easeOut"
        }} className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Clear, honest answers about how I work directly with UAE businesses to build sustainable organic search visibility, generate qualified leads, and outperform regional competitors.
          </motion.p>
        </div>

        {/* FAQ Accordion List */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2,
        ease: "easeOut"
      }} className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible defaultValue="item-1" className="space-y-4">
            {faqItems.map((item, index) => <div key={item.id} data-index={index}>
                <AccordionItem value={item.id} className="rounded-xl border border-border bg-card px-6 py-2 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/30">
                  <AccordionTrigger className="hover:no-underline py-4 text-left font-serif text-lg font-semibold text-primary">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 pr-4 text-left">
                      <span className="text-foreground text-base sm:text-lg font-medium">
                        {item.question}
                      </span>
                      <Badge variant="secondary" className="w-fit text-xs font-normal border-transparent bg-secondary text-secondary-foreground shrink-0">
                        {item.badge}
                      </Badge>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="pt-1 pb-5 text-muted-foreground text-sm sm:text-base leading-relaxed">
                    <p className="mb-4 text-foreground/90">
                      {item.answerPart1}
                      {item.linkText1 && item.linkTo1 && <Link to={item.linkTo1} className="font-medium text-primary underline underline-offset-4 transition-colors hover:text-accent">
                          {item.linkText1}
                        </Link>}
                      {item.answerPart2}
                      {item.linkText2 && item.linkTo2 && <Link to={item.linkTo2} className="font-medium text-primary underline underline-offset-4 transition-colors hover:text-accent">
                          {item.linkText2}
                        </Link>}
                      {item.answerPart3}
                    </p>

                    <div className="pt-2 border-t border-border/60">
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                        {item.bulletPoints.map((bullet, bulletIdx) => <li key={bulletIdx} data-index={bulletIdx} className="flex items-start gap-2 text-xs sm:text-sm text-foreground/80">
                            <span className="mt-1 size-2 rounded-full bg-accent shrink-0" />
                            <span>{bullet}</span>
                          </li>)}
                      </ul>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </div>)}
          </Accordion>
        </motion.div>

        {/* Bottom CTA Card */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.3,
        ease: "easeOut"
      }} className="mt-14 max-w-4xl mx-auto rounded-2xl bg-card border border-border p-8 sm:p-10 shadow-sm">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            <div className="space-y-2">
              <h3 className="text-xl sm:text-2xl font-serif font-bold text-primary">
                Have a specific question about your website in Dubai?
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground max-w-xl">
                Get in touch directly with me. I will review your current search footprint and discuss practical steps for your business.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0">
              <Button asChild size="lg" className="rounded-md font-medium">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <MessageSquare className="size-4" />
                  Ask Yassine Directly
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-md font-medium">
                <Link to="/SeoAuditsPage" className="inline-flex items-center gap-2">
                  Request an Audit
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}